import { useMemo } from 'react';
import { AnalyticsHooks, InfoTipPopover } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { productionValueDefinition } from '../../../helpers/definitions';
import { MessagingOverview } from '../../../types';
import { MessagingChartBaseInterface, MessagingChartsBase } from './charts-base';

interface Props extends MessagingChartBaseInterface {
  data: MessagingOverview['revenueGenerated'];
  dateRangeHelperText?: string;
}

export const RevenueChart = ({ data, dateRangeHelperText, ...rest }: Props) => {
  const { t } = useTranslation('analytics');
  const { averageProductionAmount } = AnalyticsHooks.useAverageProductionAmount();

  const processedData = useMemo(() => {
    const productions = {
      newPatients: (data?.newPatientsCompletedAppts ?? 0) * averageProductionAmount.value,
      existingPatients: (data?.existingPatientsCompletedAppts ?? 0) * averageProductionAmount.value,
      total: (data?.totalCompletedAppts ?? 0) * averageProductionAmount.value,
    };

    return {
      chartData: {
        centerMetric: formatters.shortenCurrency(productions.total),
        centerMetricLabel: t('Total'),
        groups: [
          {
            name: 'newPatients',
            value: productions.newPatients,
          },
          {
            name: 'existingPatients',
            value: productions.existingPatients,
          },
        ],
      },
      productions,
    };
  }, [data, t, averageProductionAmount]);

  return (
    <MessagingChartsBase
      {...rest}
      chartConfig={{
        data: processedData.chartData,
        type: 'pie',
      }}
      infoTip={
        <InfoTipPopover>{productionValueDefinition(averageProductionAmount.value, dateRangeHelperText)}</InfoTipPopover>
      }
      legendsFormatValue={formatters.shortenCurrency}
      legendsValues={processedData.productions}
      showMoreView='revenueGenerated'
      trackingIdBase='revenue-generated'
      title={t('Production Value')}
    />
  );
};
